import { cfgBrand } from '@infinitro/config';
import { translate, Verbiage } from '@infinitro/content';
import { Api, getStoredPublicKey, isSuccessStatus, loggedIn, ModalDialog, pushModal, Stage, stage, startUiTimer, Status } from '@infinitro/global';
import { stateFingerprint, stateVisit } from '@infinitro/visit';
import * as jose from 'jose';
import { get, writable } from 'svelte/store';
import { AuthConfirmEmail, AuthStartEmail, ConnectConfirmEmail, ConnectStartEmail } from './api/api';
const brand = cfgBrand();
const DEBUG = stage !== Stage.Prod;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function log(msg, data) {
    if (DEBUG)
        console.log(msg, data);
}
log(`email_verify state`);
export const stateError = writable(undefined);
export const stateFatalError = writable(false);
export const handleCheckEmailVerifyCode = async (email, otpCode, isAuthenticated) => {
    const isLoggedIn = isAuthenticated ?? get(loggedIn);
    log(`library default handleCheckEmailVerifyCode: ${email} OTP Code: ${otpCode}`);
    try {
        let data = undefined;
        if (isLoggedIn) {
            log(`call player API to confirm email verify code...`);
            const result = await ConnectConfirmEmail({
                variables: { email, code: otpCode },
                context: { api: Api.player }
            });
            data = result?.data?.connectConfirmEmail;
        }
        else {
            log(`call public API to confirm email verify code...`);
            const publicKey = await getStoredPublicKey();
            if (!publicKey) {
                const errorMessage = translate(Verbiage.public_key_not_found) +
                    ' ' +
                    translate(Verbiage.please_contact_support);
                stateError.set(errorMessage);
                stateFatalError.set(true);
                return [false, Status.FAILED, errorMessage, undefined];
            }
            const result = await AuthConfirmEmail({
                variables: {
                    email,
                    code: otpCode,
                    visitId: get(stateVisit),
                    brand,
                    publicKey,
                    fingerprint: get(stateFingerprint)
                },
                context: { api: Api.public }
            });
            data = result?.data?.authConfirmEmail;
        }
        if (data?.status === Status.MAINTENANCE) {
            pushModal(ModalDialog.FeatureUnavailable);
            return [false, Status.MAINTENANCE, undefined, undefined];
        }
        if (data?.status === Status.TOO_MANY_ATTEMPTS) {
            stateFatalError.set(true);
            const errorMessage = translate(Verbiage.too_many_attempts_please_try_again_later);
            stateError.set(errorMessage);
            return [false, Status.TOO_MANY_ATTEMPTS, errorMessage, undefined];
        }
        else if (data?.status === Status.FAILED) {
            const errorMessage = translate(Verbiage.incorrect_code) + ' ' + translate(Verbiage.please_try_again);
            stateError.set(errorMessage);
            return [false, Status.FAILED, errorMessage, undefined];
        }
        else if (data?.status === Status.CONFLICT) {
            const errorMessage = translate(Verbiage.please_contact_player_support);
            stateError.set(errorMessage);
            stateFatalError.set(true);
            return [true, Status.CONFLICT, errorMessage, undefined];
        }
        else if (isSuccessStatus(data?.status)) {
            stateFatalError.set(false);
            stateError.set(undefined);
            return [true, data?.status, undefined, data?.id];
        }
        else if (data?.status) {
            const errorMessage = data?.status ?? 'unknown error';
            stateError.set(errorMessage);
            return [isSuccessStatus(data?.status), data?.status, errorMessage, undefined];
        }
        return [false, Status.FAILED, 'unknown error', undefined];
    }
    catch (e) {
        console.warn('error checking email verify code', e);
        const errorMessage = e?.message ??
            e?.toString() ??
            'unknown error checking email verification code';
        stateError.set(errorMessage);
        return [false, Status.FAILED, errorMessage, undefined];
    }
};
export const handleRequestEmailVerifyCode = async (email, isAuthenticated) => {
    log(`library default handleRequestEmailVerifyCode: ${email}`);
    const isLoggedIn = isAuthenticated ?? get(loggedIn);
    try {
        let data = undefined;
        if (isLoggedIn) {
            const result = await ConnectStartEmail({
                variables: { email },
                context: { api: Api.player }
            });
            data = result?.data?.connectStartEmail;
        }
        else {
            const publicKey = await getStoredPublicKey();
            if (!publicKey) {
                const errorMessage = translate(Verbiage.public_key_not_found) +
                    ' ' +
                    translate(Verbiage.please_contact_support);
                stateError.set(errorMessage);
                stateFatalError.set(true);
                return [false, Status.FAILED, errorMessage];
            }
            const result = await AuthStartEmail({
                variables: {
                    email,
                    visitId: get(stateVisit),
                    brand,
                    publicKey,
                    fingerprint: get(stateFingerprint)
                },
                context: { api: Api.public }
            });
            data = result?.data?.authStartEmail;
        }
        if (data?.status === Status.MAINTENANCE) {
            pushModal(ModalDialog.FeatureUnavailable);
            return [false, Status.MAINTENANCE, undefined];
        }
        if (data?.status === Status.TOO_MANY_ATTEMPTS) {
            stateFatalError.set(true);
            const errorMessage = translate(Verbiage.too_many_attempts_please_try_again_later);
            stateError.set(errorMessage);
            return [false, Status.TOO_MANY_ATTEMPTS, errorMessage];
        }
        if (isSuccessStatus(data?.status)) {
            stateError.set(undefined);
            stateFatalError.set(false);
            if (data?.timeRemaining) {
                startUiTimer(data?.timeRemaining, data?.timeRemaining);
                log(`starting email verification timer for ${data?.timeRemaining} seconds`);
            }
            else {
                startUiTimer();
            }
        }
        return [isSuccessStatus(data?.status), data?.status, undefined];
    }
    catch (e) {
        console.warn('error requesting email verify code', e);
        const errorMessage = e?.message ??
            e?.toString() ??
            'unknown error requesting email verification code';
        stateError.set(errorMessage);
        return [false, Status.FAILED, errorMessage];
    }
};
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const onGoogleSignIn = async (response) => {
    try {
        const jwt = jose.decodeJwt(response.credential);
        if (!jwt) {
            log(`invalid Google credential: ${JSON.stringify(response, null, 4)}`);
            return;
        }
        jwt.email = jwt.email?.toLowerCase();
        // important, must be gmail or a google workspace (HD set) for Google to be authoritative
        // if not verified or not authoritative, just use as a shortcut to populate email and request email verification by code
        if (!jwt.email_verified || (!jwt.hd && !jwt.email.endsWith('@gmail.com'))) {
            log(`Google email not verified or not authoritative for ${jwt.email}`);
            // stateEmail.set(jwt.email);
            // stateShowVerifyEmail.set(true);
            return;
        }
        // stateEmail.set(jwt.email as string);
        // stateGoogleToken.set(response.credential);
        log(`Google token set: ${response.credential}`);
    }
    catch (e) {
        console.log(`error verifying Google credentails: ${e}`);
    }
};
